import { useState, useEffect, useRef } from "react";
import Editor from "@monaco-editor/react";
import { motion } from "framer-motion";
import axios from "axios";
import { FaSave } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import CandidateDashboard from "../../CandidateDashboard";
import { useInterview } from "../../../services/InterviewProgressContext";

const CodeEditor = ({ currentStep, isStoppingVideo }) => {
  /**
   * Context for the step of the interview
   */
  const { goToStep } = useInterview();
  const [question, setQuestion] = useState();
  const [questionID, SetQuestionID] = useState();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const candidateID = searchParams.get("candidate_id");
  const [isSaving, setIsSaving] = useState(false);

  const [compilerOne, setCompilerOne] = useState({
    userCode: "\n\n",
    language: "python",
    output: "",
  });

  const [compilerTwo, setCompilerTwo] = useState({
    userCode: "\n\n",
    language: "javascript",
    output: "",
  });

  const languageMap = {
    python: 71,
    javascript: 63,
    nodejs: 63,
    html: 80,
    c: 50,
    cpp: 54,
    java: 62,
    php: 68,
    csharp: 51,
    assembly: 45,
    lua: 64,
    plsql: 85,
    mysql: 82,
    postgresql: 83,
    mongodb: 83,
    groovy: 85,
    ruby: 72,
    jquery: 80,
  };

  const handleRun = async (compiler, setCompiler) => {
    setCompiler((prev) => ({ ...prev, output: "Running..." }));

    if (compiler.language === "html" || compiler.language === "jquery") {
      document.getElementById("html-output").srcdoc = compiler.userCode;
      setCompiler((prev) => ({
        ...prev,
        output: "HTML & jQuery executed below.",
      }));
      return;
    }

    const options = {
      method: "POST",
      url: "https://judge0-ce.p.rapidapi.com/submissions",
      params: { base64_encoded: "false", fields: "*" },
      headers: {
        "content-type": "application/json",
        "X-RapidAPI-Key": "7babc97970msh5d6affb18e2ed85p1079cajsn9048eb479b41",
        "X-RapidAPI-Host": "judge0-ce.p.rapidapi.com",
      },
      data: {
        language_id: languageMap[compiler.language],
        source_code: compiler.userCode.trim(),
      },
    };

    try {
      const response = await axios.request(options);
      const token = response.data.token;
      setTimeout(async () => {
        const result = await axios.get(
          `https://judge0-ce.p.rapidapi.com/submissions/${token}?base64_encoded=false`,
          {
            headers: {
              "X-RapidAPI-Key":
                "7babc97970msh5d6affb18e2ed85p1079cajsn9048eb479b41",
              "X-RapidAPI-Host": "judge0-ce.p.rapidapi.com",
            },
          }
        );
        setCompiler((prev) => ({
          ...prev,
          output: result.data.stdout || result.data.stderr || "No output",
        }));
      }, 3000);
    } catch (error) {
      setCompiler((prev) => ({ ...prev, output: "❌ Error executing code." }));
    }
  };

  useEffect(() => {
    generateQuestion();
  }, []);

  const generateQuestion = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_KEY}/get_coding_question?jd_id=${id}&candidate_id=${candidateID}`,
        { headers: { "Content-Type": "application/json" } }
      );
      setQuestion(response?.data?.question);
      SetQuestionID(response?.data?.id);
    } catch (error) {
      await SetInterviewStatus();
    }
  };

  const handleSaveBothCompilers = async () => {
    setIsSaving(true);

    const payload = {
      candidate_id: candidateID,
      jd_id: id,
      question_id: questionID,
      problem_statement: question || "Problem statement not found.",
      compilers: [
        {
          compiler: 1,
          language: compilerOne.language,
          code: compilerOne.userCode,
        },
        {
          compiler: 2,
          language: compilerTwo.language,
          code: compilerTwo.userCode,
        },
      ],
    };

    try {
      await axios.post(
        `${process.env.REACT_APP_API_KEY}/submit_code?jd_id=${id}&candidate_id=${candidateID}&question_id=${questionID}`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      isStoppingVideo.current = true;
      await SetInterviewStatus();
    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  };

  const SetInterviewStatus = async () => {
    const payload = {
      interview_status: "Completed",
    };
    try {
      await axios.post(
        `${process.env.REACT_APP_API_KEY}/set_interview_status?jd_id=${id}&candidate_id=${candidateID}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      goToStep("completed");
    } catch (error) {}
  };

  // const handleDownloadReport = async () => {
  //   try {
  //     const response = await axios.get(
  //       `${process.env.REACT_APP_API_KEY}/download_candidate_zip?jd_id=${id}&candidate_id=${candidateID}`
  //     );

  //     const fileUrl = response.data.link;
  //     const link = document.createElement("a");
  //     link.href = fileUrl;
  //     link.setAttribute("download", `interview_report_${id}.zip`);
  //     document.body.appendChild(link);
  //     link.click();
  //     link.remove();
  //   } catch (error) {
  //     console.error("Error downloading report:", error);
  //     alert("❌ Failed to download report. Please try again later.");
  //   }
  // };

  const handleDownloadReport = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_KEY}/download_candidate_zip?jd_id=${id}&candidate_id=${candidateID}`,
        {
          responseType: "blob", // important for binary files
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Expires: "0",
          },
        }
      );

      // Create a URL from the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));

      // Create a temporary anchor element to trigger download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `interview_report_${id}.zip`); // default file name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up URL object
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading report:", error);
      alert("❌ Failed to download report. Please try again later.");
    }
  };

  useEffect(() => {});

  if (currentStep == "completed" || currentStep == "reportGenerated") {
    return (
      <div className="w-full flex flex-col items-center justify-center space-y-6">
        <h1 className="text-4xl font-bold text-white bg-black px-8 bg-opacity-50 py-6 rounded-lg shadow-2xl text-center">
          Interview Completed! <br />
          <span className="text-lg text-gray-200">
            You can now download your report.
          </span>
        </h1>

        {currentStep == "reportGenerated" ? (
          <motion.button
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg shadow-md flex items-center gap-2"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleDownloadReport}
          >
            Download Report
          </motion.button>
        ) : (
          <div>Creating your report...</div>
        )}
        <div className="absolute -top-[9999px] left-0 opacity-0 pointer-events-none">
          <CandidateDashboard jd_id={id} candidate_id={candidateID} />
        </div>
      </div>
    );
  }

  return (
    <>
      {question?.length > 0 && (
        <div>
          <div className="bg-gray-800 p-8">
            <h3 className="font-semibold text-lg text-gray-300">Question:</h3>
            <p className="text-gray-300">{question}</p>
          </div>
          <div className="w-full h-[85vh] flex flex-col md:flex-row">
            {[compilerOne, compilerTwo].map((compiler, index) => (
              <div
                key={index}
                className="p-4 bg-gray-900 text-white h-full border-r-8 border-gray-700 w-1/2"
              >
                <select
                  className="bg-gray-700 p-1 mt-2 mb-1 rounded w-full"
                  value={compiler.language}
                  onChange={(e) => {
                    const newLang = e.target.value;
                    index === 0
                      ? setCompilerOne((prev) => ({
                          ...prev,
                          language: newLang,
                        }))
                      : setCompilerTwo((prev) => ({
                          ...prev,
                          language: newLang,
                        }));
                  }}
                >
                  {Object.keys(languageMap).map((lang) => (
                    <option key={lang} value={lang}>
                      {lang.toUpperCase()}
                    </option>
                  ))}
                </select>

                <Editor
                  height="300px"
                  language={
                    compiler.language === "nodejs"
                      ? "javascript"
                      : compiler.language
                  }
                  theme="vs-dark"
                  value={compiler.userCode}
                  onChange={(value) => {
                    index === 0
                      ? setCompilerOne((prev) => ({ ...prev, userCode: value }))
                      : setCompilerTwo((prev) => ({
                          ...prev,
                          userCode: value,
                        }));
                  }}
                  options={{ wordWrap: "on", minimap: { enabled: false } }}
                />

                <button
                  onClick={() =>
                    handleRun(
                      compiler,
                      index === 0 ? setCompilerOne : setCompilerTwo
                    )
                  }
                  className="mt-4 bg-blue-500 px-4 py-2 rounded w-full"
                >
                  Run Code
                </button>

                <div className="mt-4 p-3 bg-gray-800 rounded">
                  <h3 className="text-lg font-semibold">Output:</h3>
                  <pre className="whitespace-pre-wrap">
                    {compiler.output || "Click Run to execute code"}
                  </pre>

                  {(compiler.language === "html" ||
                    compiler.language === "jquery") && (
                    <iframe
                      id="html-output"
                      title="HTML Output"
                      className="w-full mt-4 h-64 bg-white"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="bg-gray-900 p-2 flex justify-center">
            <motion.button
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg shadow-md flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSaveBothCompilers}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <FaSave />
                  Save
                </>
              )}
            </motion.button>
          </div>
        </div>
      )}
    </>
  );
};

export default CodeEditor;
